import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add,
  Today,
  CalendarMonth,
  ViewWeek,
  ViewDay,
  Edit,
  Delete,
  LocationOn,
  AccessTime,
  People,
  VideoCall,
  Cake,
} from "@mui/icons-material";
import APIKit from "../../../config/APIKit";
import { showSnackBar } from "../../store/compatibility";
import { useDispatch } from "react-redux";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  isToday,
} from "date-fns";
import style from "../settings/settings.module.scss";
import Title from "../settings/Title";
import ConfirmationDialog from "../../component/ConfirmationDialog";

const EVENT_TYPES = {
  meeting: { label: "Meeting", color: "#2196f3" },
  appointment: { label: "Appointment", color: "#4caf50" },
  call: { label: "Call", color: "#ff9800" },
  task: { label: "Task", color: "#9c27b0" },
  reminder: { label: "Reminder", color: "#f44336" },
  birthday: { label: "Birthday", color: "#e91e63" },
  other: { label: "Other", color: "#607d8b" },
};

export default function CalendarView() {
  const dispatch = useDispatch();
  const [view, setView] = useState("month"); // month, week, day
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventModal, setEventModal] = useState({
    open: false,
    event: null,
    mode: "create",
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    loading: false,
  });

  useEffect(() => {
    loadEvents();
  }, [currentDate, view]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      let url = "/calendar/events";
      const params = new URLSearchParams();

      if (view === "month") {
        params.append("view", "month");
        params.append("date", format(currentDate, "yyyy-MM-dd"));
      } else if (view === "week") {
        params.append("view", "week");
        params.append("date", format(currentDate, "yyyy-MM-dd"));
      } else {
        params.append("view", "day");
        params.append("date", format(currentDate, "yyyy-MM-dd"));
      }

      const response = await APIKit.get(`${url}?${params.toString()}`);
      if (response && (response.success || response.status)) {
        const eventsData = response.data || response.data?.data || [];
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Error loading events:", error);
      dispatch(
        showSnackBar({ message: "Error loading events", severity: "error" }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEventModal({ open: true, event: null, mode: "create" });
  };

  const handleEditEvent = (event) => {
    setEventModal({ open: true, event, mode: "edit" });
  };

  const openConfirmDialog = (eventId) => {
    setConfirmDialog({
      open: true,
      id: eventId,
      loading: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      id: null,
      loading: false,
    });
  };

  const handleDeleteEvent = async () => {
    if (!confirmDialog.id) return;

    setConfirmDialog((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.delete(`/calendar/events/${confirmDialog.id}`);
      dispatch(
        showSnackBar({
          message: "Event deleted successfully",
          severity: "success",
        }),
      );
      loadEvents();
      closeConfirmDialog();
    } catch (error) {
      dispatch(
        showSnackBar({ message: "Error deleting event", severity: "error" }),
      );
      setConfirmDialog((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const renderMonthView = () => {
    // Full month calendar view
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 }); // Sunday
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const getEventsForDate = (date) => {
      return events.filter((event) => {
        const eventDate = new Date(event.start_time);
        return isSameDay(eventDate, date);
      });
    };

    return (
      <Box
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          background: "#ffffff",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          position: "relative",
        }}
      >
        {/* Month Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            background: "#ffffff",
          }}
        >
          <IconButton
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              p: 1,
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
              ←
            </Typography>
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#333",
              fontSize: "18px",
              letterSpacing: "0.3px",
            }}
          >
            {format(currentDate, "MMMM yyyy")}
          </Typography>
          <IconButton
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              p: 1,
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
              →
            </Typography>
          </IconButton>
        </Box>

        {/* Week Day Headers */}
        <Grid container>
          {weekDays.map((day, index) => (
            <Grid
              // item
              // xs={12 / 7}
              size={{ xs: 12 / 7 }}
              key={day}
              sx={{
                py: 1.5,
                textAlign: "center",
                fontWeight: 600,
                color: "#666",
                fontSize: "0.875rem",
                background: "#f8f9fa",
                borderRight:
                  index < 6 ? "1px solid rgba(0, 0, 0, 0.06)" : "none",
                borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
              }}
            >
              {day}
            </Grid>
          ))}
        </Grid>

        {/* Full Month Calendar Grid */}
        <Grid container>
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);
            const isSelected = isSameDay(day, selectedDate);
            const dayOfWeek = index % 7;

            return (
              <Grid
                // item
                // xs={12 / 7}
                size={{ xs: 12 / 7 }}
                key={day.toISOString()}
                sx={{
                  minHeight: { xs: "100px", sm: "120px", md: "140px" },
                  borderRight:
                    dayOfWeek < 6 ? "1px solid rgba(0, 0, 0, 0.06)" : "none",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
                  p: { xs: 0.75, sm: 1, md: 1.5 },
                  background: isCurrentDay
                    ? "rgba(102, 126, 234, 0.1)"
                    : isSelected
                      ? "rgba(102, 126, 234, 0.05)"
                      : "#ffffff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    background: isCurrentDay
                      ? "rgba(102, 126, 234, 0.15)"
                      : isSelected
                        ? "rgba(102, 126, 234, 0.08)"
                        : "rgba(0, 0, 0, 0.02)",
                  },
                }}
                onClick={() => {
                  setSelectedDate(day);
                  if (dayEvents.length > 0 || isCurrentMonth) {
                    setView("day");
                    setCurrentDate(day);
                  }
                }}
              >
                {/* Date Number */}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isCurrentDay ? 700 : isSelected ? 600 : 400,
                    color: isCurrentMonth
                      ? isCurrentDay
                        ? "#667eea"
                        : "#1a1a1a"
                      : "#d0d0d0",
                    mb: 0.75,
                    fontSize: "0.875rem",
                    alignSelf: "flex-start",
                    lineHeight: 1.2,
                  }}
                >
                  {format(day, "d")}
                </Typography>

                {/* Events */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: 1,
                    width: "100%",
                    overflow: "hidden",
                    minHeight: 0,
                  }}
                >
                  {dayEvents.slice(0, 2).map((event, eventIndex) => {
                    const eventType =
                      EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
                    return (
                      <Box
                        key={event.id || eventIndex}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent(event);
                        }}
                        sx={{
                          background: eventType.color,
                          color: "#fff",
                          borderRadius: "6px",
                          px: 0.75,
                          py: 0.4,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          transition: "all 0.2s ease",
                          width: "100%",
                          lineHeight: 1.3,
                          "&:hover": {
                            opacity: 0.9,
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        {event.title}
                      </Box>
                    );
                  })}
                  {dayEvents.length > 2 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667eea",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        mt: 0.25,
                        textAlign: "left",
                        lineHeight: 1.2,
                      }}
                    >
                      +{dayEvents.length - 2} more
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
    const weekDays = eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(currentDate, { weekStartsOn: 0 }),
    });

    const getEventsForDate = (date) => {
      return events.filter((event) => {
        const eventDate = new Date(event.start_time);
        return isSameDay(eventDate, date);
      });
    };

    return (
      <Box
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          background: "#ffffff",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          position: "relative",
        }}
      >
        {/* Week Header */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            background: "#ffffff",
          }}
        >
          <IconButton
            onClick={() => setCurrentDate(subWeeks(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            ←
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#333",
              fontSize: "18px",
            }}
          >
            {format(weekStart, "MMM d")} -{" "}
            {format(weekDays[weekDays.length - 1], "MMM d, yyyy")}
          </Typography>
          <IconButton
            onClick={() => setCurrentDate(addWeeks(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            →
          </IconButton>
        </Box>

        {/* Week Days Grid */}
        <Grid container>
          {weekDays.map((day) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentDay = isToday(day);
            const isSelected = isSameDay(day, selectedDate);

            return (
              <Grid
                // item
                // xs={12 / 7}
                size={{ xs: 12 / 7 }}
                key={day.toISOString()}
                sx={{
                  borderRight: "1px solid rgba(0, 0, 0, 0.06)",
                  minHeight: "400px",
                  p: 2,
                  background: isCurrentDay
                    ? "rgba(102, 126, 234, 0.05)"
                    : "#ffffff",
                  "&:last-child": {
                    borderRight: "none",
                  },
                }}
                onClick={() => setSelectedDate(day)}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: isCurrentDay ? 700 : 600,
                    color: isCurrentDay ? "#667eea" : "#666",
                    mb: 1,
                    textAlign: "center",
                    fontSize: "0.813rem",
                  }}
                >
                  {format(day, "EEE")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: isCurrentDay ? 700 : 600,
                    color: isCurrentDay ? "#667eea" : "#333",
                    mb: 2,
                    textAlign: "center",
                    fontSize: "18px",
                  }}
                >
                  {format(day, "d")}
                </Typography>
                <Box>
                  {dayEvents.map((event) => {
                    const eventType =
                      EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
                    return (
                      <Box
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditEvent(event);
                        }}
                        sx={{
                          mb: 1,
                          p: 1.5,
                          background: eventType.color,
                          color: "#fff",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            opacity: 0.9,
                            transform: "translateX(4px)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            display: "block",
                            mb: 0.5,
                            opacity: 0.95,
                          }}
                        >
                          {format(new Date(event.start_time), "h:mm a")}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.title}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderDayView = () => {
    const dayEvents = events
      .filter((event) => {
        const eventDate = new Date(event.start_time);
        return isSameDay(eventDate, currentDate);
      })
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    return (
      <Box
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          background: "#ffffff",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          position: "relative",
        }}
      >
        {/* Day Header */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            background: "#ffffff",
          }}
        >
          <IconButton
            onClick={() => setCurrentDate(subDays(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            ←
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#333",
              fontSize: "18px",
            }}
          >
            {format(currentDate, "EEEE, MMMM d, yyyy")}
          </Typography>
          <IconButton
            onClick={() => setCurrentDate(addDays(currentDate, 1))}
            sx={{
              color: "#666",
              borderRadius: "8px",
              "&:hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            →
          </IconButton>
        </Box>

        {/* Day Events */}
        <Box sx={{ p: 3 }}>
          {dayEvents.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No events scheduled for this day
            </Typography>
          ) : (
            dayEvents.map((event) => {
              const eventType =
                EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
              return (
                <Card
                  key={event.id}
                  sx={{
                    mb: 2,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                    border: `2px solid ${eventType.color}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="start"
                    >
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            label={eventType.label}
                            size="small"
                            sx={{
                              background: eventType.color,
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: "#333",
                            }}
                          >
                            {event.title}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <AccessTime
                            fontSize="small"
                            sx={{ color: "#667eea" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: "#666" }}
                          >
                            {event.is_all_day
                              ? "All Day"
                              : `${format(new Date(event.start_time), "h:mm a")} - ${format(new Date(event.end_time), "h:mm a")}`}
                          </Typography>
                        </Box>
                        {event.location && (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            <LocationOn
                              fontSize="small"
                              sx={{ color: "#667eea" }}
                            />
                            <Typography variant="body2" sx={{ color: "#666" }}>
                              {event.location}
                            </Typography>
                          </Box>
                        )}
                        {event.description && (
                          <Typography
                            variant="body2"
                            sx={{ color: "#999", mt: 1 }}
                          >
                            {event.description}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditEvent(event)}
                          sx={{
                            color: "#667eea",
                            "&:hover": {
                              background: "rgba(102, 126, 234, 0.1)",
                            },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openConfirmDialog(event.id)}
                          sx={{
                            color: "#f44336",
                            "&:hover": {
                              background: "rgba(244, 67, 54, 0.1)",
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      </Box>
    );
  };

  const renderEventList = () => {
    const sortedEvents = [...events].sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time),
    );

    return (
      <Box>
        {sortedEvents.length === 0 ? (
          <Box
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow:
                "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid rgba(102, 126, 234, 0.1)",
              position: "relative",
              p: 4,
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  "linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                zIndex: 1,
              },
            }}
          >
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No events scheduled
            </Typography>
          </Box>
        ) : (
          sortedEvents.map((event) => (
            <Card
              key={event.id}
              sx={{
                mb: 2,
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow:
                  "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                border: "1px solid rgba(102, 126, 234, 0.1)",
                position: "relative",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow:
                    "0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: `linear-gradient(90deg, ${EVENT_TYPES[event.event_type]?.color || "#607d8b"} 0%, ${EVENT_TYPES[event.event_type]?.color || "#607d8b"} 100%)`,
                  zIndex: 1,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="start"
                >
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      {event.event_type === "birthday" && (
                        <Box
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%)",
                            p: 1,
                            borderRadius: "10px",
                            color: EVENT_TYPES.birthday.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Cake sx={{ fontSize: 20 }} />
                        </Box>
                      )}
                      <Chip
                        label={
                          EVENT_TYPES[event.event_type]?.label ||
                          event.event_type
                        }
                        size="small"
                        sx={{
                          background: `linear-gradient(135deg, ${EVENT_TYPES[event.event_type]?.color || "#607d8b"} 0%, ${EVENT_TYPES[event.event_type]?.color || "#607d8b"} 100%)`,
                          color: "white",
                          fontWeight: 600,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        {event.title}
                      </Typography>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={1} mb={1.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTime
                          fontSize="small"
                          sx={{ color: "#667eea" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#666" }}
                        >
                          {event.is_all_day || event.event_type === "birthday"
                            ? format(new Date(event.start_time), "MMM d, yyyy")
                            : `${format(new Date(event.start_time), "MMM d, yyyy h:mm a")} - ${format(new Date(event.end_time), "h:mm a")}`}
                        </Typography>
                        {(event.is_all_day ||
                          event.event_type === "birthday") && (
                          <Chip
                            label="All Day"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(102, 126, 234, 0.3)",
                              color: "#667eea",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>

                      {event.location && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn
                            fontSize="small"
                            sx={{ color: "#667eea" }}
                          />
                          <Typography variant="body2" sx={{ color: "#666" }}>
                            {event.location}
                          </Typography>
                        </Box>
                      )}

                      {event.meeting_url && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <VideoCall
                            fontSize="small"
                            sx={{ color: "#667eea" }}
                          />
                          <Typography
                            variant="body2"
                            component="a"
                            href={event.meeting_url}
                            target="_blank"
                            sx={{
                              color: "#667eea",
                              textDecoration: "none",
                              fontWeight: 600,
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            Join Meeting
                          </Typography>
                        </Box>
                      )}

                      {event.Attendees && event.Attendees.length > 0 && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <People fontSize="small" sx={{ color: "#667eea" }} />
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", fontWeight: 600 }}
                          >
                            {event.Attendees.length} attendee
                            {event.Attendees.length > 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      )}

                      {event.is_birthday && event.birthday_user && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Cake fontSize="small" sx={{ color: "#667eea" }} />
                          <Typography
                            variant="body2"
                            sx={{ color: "#666", fontWeight: 600 }}
                          >
                            {event.birthday_user.first_name}{" "}
                            {event.birthday_user.last_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {event.description && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#999", fontSize: "13px" }}
                      >
                        {event.description}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    {!event.is_birthday && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEditEvent(event)}
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                            color: "#667eea",
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            mb: 0.5,
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "#ffffff",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                            },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openConfirmDialog(event.id)}
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)",
                            color: "#ef5350",
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #ef5350 0%, #e53935 100%)",
                              color: "#ffffff",
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(239, 83, 80, 0.3)",
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    {event.is_birthday && (
                      <Tooltip title="Birthday events are auto-generated and cannot be edited">
                        <Chip
                          label="Auto"
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.7rem",
                            borderColor: "rgba(102, 126, 234, 0.3)",
                            color: "#667eea",
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );
  };

  return (
    <div className={style.container}>
      {/* Modern Purple Gradient Header */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
          borderRadius: "16px",
          p: 3,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            fontSize: "28px",
            letterSpacing: "-0.5px",
            position: "relative",
            zIndex: 1,
          }}
        >
          | Calendar
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateEvent}
          sx={{
            background: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            color: "#ffffff",
            fontWeight: 700,
            borderRadius: "12px",
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontSize: "15px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            position: "relative",
            zIndex: 1,
            "&:hover": {
              background: "rgba(255, 255, 255, 0.3)",
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Add
        </Button>
      </Box>

      <Box>
        {/* View Controls */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 2,
          }}
        >
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <Button
              variant={view === "month" ? "contained" : "outlined"}
              startIcon={<CalendarMonth />}
              onClick={() => setView("month")}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "14px",
                px: 2.5,
                py: 1,
                ...(view === "month"
                  ? {
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
                      color: "#ffffff",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                        transform: "translateY(-2px)",
                      },
                    }
                  : {
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      color: "#666",
                      background: "#ffffff",
                      "&:hover": {
                        borderColor: "rgba(102, 126, 234, 0.5)",
                        background: "rgba(102, 126, 234, 0.05)",
                      },
                    }),
                transition: "all 0.3s ease",
              }}
            >
              Month
            </Button>
            <Button
              variant={view === "week" ? "contained" : "outlined"}
              startIcon={<ViewWeek />}
              onClick={() => setView("week")}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "14px",
                px: 2.5,
                py: 1,
                ...(view === "week"
                  ? {
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
                      color: "#ffffff",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                        transform: "translateY(-2px)",
                      },
                    }
                  : {
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      color: "#666",
                      background: "#ffffff",
                      "&:hover": {
                        borderColor: "rgba(102, 126, 234, 0.5)",
                        background: "rgba(102, 126, 234, 0.05)",
                      },
                    }),
                transition: "all 0.3s ease",
              }}
            >
              Week
            </Button>
            <Button
              variant={view === "day" ? "contained" : "outlined"}
              startIcon={<ViewDay />}
              onClick={() => setView("day")}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "14px",
                px: 2.5,
                py: 1,
                ...(view === "day"
                  ? {
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
                      color: "#ffffff",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                        transform: "translateY(-2px)",
                      },
                    }
                  : {
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      color: "#666",
                      background: "#ffffff",
                      "&:hover": {
                        borderColor: "rgba(102, 126, 234, 0.5)",
                        background: "rgba(102, 126, 234, 0.05)",
                      },
                    }),
                transition: "all 0.3s ease",
              }}
            >
              Day
            </Button>
          </Box>

          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<Today />}
              onClick={() => {
                setCurrentDate(new Date());
                setSelectedDate(new Date());
              }}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                borderColor: "rgba(0, 0, 0, 0.12)",
                color: "#666",
                background: "#ffffff",
                px: 2.5,
                py: 1,
                "&:hover": {
                  borderColor: "rgba(102, 126, 234, 0.5)",
                  background: "rgba(102, 126, 234, 0.05)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Today
            </Button>
          </Box>
        </Box>

        {/* Calendar View */}
        {loading ? (
          <Box
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow:
                "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: "1px solid rgba(102, 126, 234, 0.1)",
              position: "relative",
              p: 4,
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  "linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                zIndex: 1,
              },
            }}
          >
            <Typography align="center" sx={{ py: 4, color: "#666" }}>
              Loading...
            </Typography>
          </Box>
        ) : view === "month" ? (
          renderMonthView()
        ) : view === "week" ? (
          renderWeekView()
        ) : view === "day" ? (
          renderDayView()
        ) : (
          renderEventList()
        )}
      </Box>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={handleDeleteEvent}
        title="Delete Event?"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        confirmColor="error"
        loading={confirmDialog.loading}
      />

      {/* Event Modal */}
      <EventModal
        open={eventModal.open}
        event={eventModal.event}
        mode={eventModal.mode}
        selectedDate={eventModal.selectedDate}
        onClose={() =>
          setEventModal({ open: false, event: null, mode: "create" })
        }
        onSave={loadEvents}
      />
    </div>
  );
}

// Event Create/Edit Modal Component
function EventModal({ open, event, mode, selectedDate, onClose, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "meeting",
    start_time: selectedDate || new Date(),
    end_time: selectedDate
      ? new Date(selectedDate.getTime() + 3600000)
      : new Date(Date.now() + 3600000),
    location: "",
    location_type: "physical",
    meeting_url: "",
    priority: "medium",
    is_all_day: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || "",
        description: event.description || "",
        event_type: event.event_type || "meeting",
        start_time: event.start_time ? new Date(event.start_time) : new Date(),
        end_time: event.end_time ? new Date(event.end_time) : new Date(),
        location: event.location || "",
        location_type: event.location_type || "physical",
        meeting_url: event.meeting_url || "",
        priority: event.priority || "medium",
        is_all_day: event.is_all_day || false,
      });
    } else if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        start_time: selectedDate,
        end_time: new Date(selectedDate.getTime() + 3600000),
      }));
    }
  }, [event, selectedDate]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        start_time: format(formData.start_time, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        end_time: format(formData.end_time, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
      };

      if (mode === "create") {
        await APIKit.post("/calendar/events", payload);
        dispatch(
          showSnackBar({
            message: "Event created successfully",
            severity: "success",
          }),
        );
      } else {
        await APIKit.post(`/calendar/events/${event.id}`, payload);
        dispatch(
          showSnackBar({
            message: "Event updated successfully",
            severity: "success",
          }),
        );
      }

      onSave();
      onClose();
    } catch (error) {
      dispatch(
        showSnackBar({ message: "Error saving event", severity: "error" }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          boxShadow:
            "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "20px",
          py: 2.5,
          px: 3,
          mb: 0,
        }}
      >
        {mode === "create" ? "Create Event" : "Edit Event"}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3, overflow: "visible" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              fullWidth
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                    },
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#667eea",
                },
              }}
            />
          </Box>
          <Box>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              fullWidth
              multiline
              minRows={3}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                    },
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#667eea",
                },
              }}
            />
          </Box>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="event-type-label"
                    sx={{
                      "&.MuiInputLabel-root": {
                        backgroundColor: "transparent",
                        px: 0.5,
                        transform: "translate(14px, 16px) scale(1)",
                      },
                      "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, -9px) scale(0.75)",
                      },
                    }}
                  >
                    Event Type
                  </InputLabel>
                  <Select
                    labelId="event-type-label"
                    value={formData.event_type}
                    onChange={(e) =>
                      setFormData({ ...formData, event_type: e.target.value })
                    }
                    label="Event Type"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                          boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                        },
                      },
                    }}
                  >
                    {Object.keys(EVENT_TYPES).map((type) => (
                      <MenuItem key={type} value={type}>
                        {EVENT_TYPES[type].label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="priority-label"
                    sx={{
                      "&.MuiInputLabel-root": {
                        backgroundColor: "transparent",
                        px: 0.5,
                        transform: "translate(14px, 16px) scale(1)",
                      },
                      "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, -9px) scale(0.75)",
                      },
                    }}
                  >
                    Priority
                  </InputLabel>
                  <Select
                    labelId="priority-label"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: e.target.value })
                    }
                    label="Priority"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                        },
                      },
                      "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                          boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="Start Date & Time"
                  type="datetime-local"
                  value={format(formData.start_time, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setFormData({ ...formData, start_time: newDate });
                  }}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                        },
                      },
                    },
                    "& .MuiOutlinedInput-root.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea",
                        borderWidth: "2px",
                        boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="End Date & Time"
                  type="datetime-local"
                  value={format(formData.end_time, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setFormData({ ...formData, end_time: newDate });
                  }}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#667eea",
                          borderWidth: "2px",
                        },
                      },
                    },
                    "& .MuiOutlinedInput-root.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#667eea",
                        borderWidth: "2px",
                        boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Location"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                    },
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#667eea",
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Meeting URL"
              value={formData.meeting_url}
              onChange={(e) =>
                setFormData({ ...formData, meeting_url: e.target.value })
              }
              fullWidth
              placeholder="https://zoom.us/j/..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#667eea",
                      borderWidth: "2px",
                      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
                    },
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#667eea",
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_all_day}
                  onChange={(e) =>
                    setFormData({ ...formData, is_all_day: e.target.checked })
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#667eea",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#667eea",
                    },
                  }}
                />
              }
              label="All Day"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontWeight: 600,
                  color: "#667eea",
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: "rgba(0, 0, 0, 0.2)",
            "&:hover": {
              borderColor: "rgba(0, 0, 0, 0.4)",
              background: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 16px rgba(102, 126, 234, 0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
              transform: "translateY(-2px)",
            },
            "&:disabled": {
              background: "rgba(102, 126, 234, 0.5)",
            },
          }}
        >
          {loading
            ? mode === "create"
              ? "Creating..."
              : "Updating..."
            : mode === "create"
              ? "Create"
              : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
